import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card } from "../components/ui";
import { LoadingSpinner } from "../components";
import { API_BASE_URL, IVPositionFilterOptions, LoadingSpinnerMeta } from "../data";
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from "../icons";
import { DirectorVolunteerMetaProps, DirectorVolunteerRecord } from "../data/types";

type Props = {
    meta: DirectorVolunteerMetaProps
}

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

type Filters = {
    name: string
    phone: string
    service_record: string[]
    age_start: number | null
    age_end: number | null
    gender: string
}

const ITEMS_PER_PAGE = 10

const emptyFilters: Filters = { name: '', phone: '', service_record: [], age_start: null, age_end: null, gender: '' }

const GENDER_OPTIONS = ['All', 'Male', 'Female']

const defaultPagination: PaginationData = {
    current_page: 1,
    last_page: 1,
    per_page: ITEMS_PER_PAGE,
    total: 0
}

// Directors only need the positions that distinguish uniformed/non-uniformed/junior
// volunteers; exec-level-only categories don't apply to this view.
const IV_POSITION_OPTIONS = IVPositionFilterOptions.filter(
    (option) => !['IV Executive', 'IV Events', 'Other'].includes(option)
)

export const DirectorVolunteer = ({ meta }: Props) => {
    const [volunteers, setVolunteers] = useState<DirectorVolunteerRecord[]>([])
    const [pagination, setPagination] = useState<PaginationData>(defaultPagination)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [nameFilter, setNameFilter] = useState('')
    const [phoneFilter, setPhoneFilter] = useState('')
    const [positionFilter, setPositionFilter] = useState<string[]>([])
    const [ageStartFilter, setAgeStartFilter] = useState<number | null>(null)
    const [ageEndFilter, setAgeEndFilter] = useState<number | null>(null)
    const [genderFilter, setGenderFilter] = useState('')
    const [appliedFilters, setAppliedFilters] = useState<Filters>(emptyFilters)

    const hasFetched = useRef(false)

    const fetchVolunteers = async (page: number, filters: Filters) => {
        setLoading(true)
        setError(null)

        try {
            const params = new URLSearchParams({
                per_page: String(ITEMS_PER_PAGE),
                page: String(page),
            })
            if (filters.name) params.append('name', filters.name)
            if (filters.phone) params.append('phone', filters.phone)
            if (filters.service_record.length > 0) {
                params.append('service_record', JSON.stringify(filters.service_record))
            }
            if (filters.age_start !== null) params.append('age_start', String(filters.age_start))
            if (filters.age_end !== null) params.append('age_end', String(filters.age_end))
            if (filters.gender) params.append('genders', JSON.stringify([filters.gender]))

            const response = await fetch(`${API_BASE_URL}/volunteer/director?${params.toString()}`, {
                method: 'GET',
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error('Failed to retrieve volunteers.')
            }

            const body = await response.json()
            setVolunteers(body.data ?? [])
            setPagination({
                current_page: body.current_page,
                last_page: body.last_page,
                per_page: body.per_page,
                total: body.total
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to retrieve volunteers.')
            setVolunteers([])
            setPagination(defaultPagination)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true
            fetchVolunteers(1, emptyFilters)
        }
    }, [])

    const handleSearch = () => {
        const filters: Filters = {
            name: nameFilter.trim(),
            phone: phoneFilter.trim(),
            service_record: positionFilter,
            age_start: ageStartFilter,
            age_end: ageEndFilter,
            gender: genderFilter
        }
        setAppliedFilters(filters)
        fetchVolunteers(1, filters)
    }

    const handleClear = () => {
        setNameFilter('')
        setPhoneFilter('')
        setPositionFilter([])
        setAgeStartFilter(null)
        setAgeEndFilter(null)
        setGenderFilter('')
        setAppliedFilters(emptyFilters)
        fetchVolunteers(1, emptyFilters)
    }

    const handleFilterKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch()
    }

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchVolunteers(page, appliedFilters)
        }
    }

    const togglePosition = (position: string) => {
        setPositionFilter(prev =>
            prev.includes(position) ? prev.filter(p => p !== position) : [...prev, position]
        )
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div>
                <Link to="/admin" className="inline-flex items-center mb-8 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
                    <ChevronLeftIcon className="w-5 h-5 me-2" />
                    Back to Admin Hub
                </Link>
            </div>

            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{meta.title}</h1>
                <p className="mt-1 text-gray-600 dark:text-gray-400">{meta.description}</p>
            </div>

            <Card className="p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <input
                        type="text"
                        placeholder="Search by name..."
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        onKeyDown={handleFilterKeyDown}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <input
                        type="text"
                        placeholder="Search by phone number..."
                        value={phoneFilter}
                        onChange={(e) => setPhoneFilter(e.target.value)}
                        onKeyDown={handleFilterKeyDown}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                            Gender
                        </label>
                        <select
                            value={genderFilter}
                            onChange={(e) => setGenderFilter(e.target.value === 'All' ? '' : e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        >
                            {GENDER_OPTIONS.map(option => (
                                <option key={option} value={option === 'All' ? '' : option}>{option}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                        Age Range
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="0"
                            placeholder="Min"
                            value={ageStartFilter ?? ''}
                            onChange={(e) => setAgeStartFilter(e.target.value ? Number(e.target.value) : null)}
                            onKeyDown={handleFilterKeyDown}
                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                        />
                        <span className="text-gray-500 dark:text-gray-400 text-sm">to</span>
                        <input
                            type="number"
                            min="0"
                            placeholder="Max"
                            value={ageEndFilter ?? ''}
                            onChange={(e) => setAgeEndFilter(e.target.value ? Number(e.target.value) : null)}
                            onKeyDown={handleFilterKeyDown}
                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                        />
                    </div>
                </div>

                <div className="mb-4">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                        IV Position
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 flex flex-wrap gap-4">
                        {IV_POSITION_OPTIONS.map(option => (
                            <label key={option} className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={positionFilter.includes(option)}
                                    onChange={() => togglePosition(option)}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-200">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex gap-2">
                    <Button onClick={handleSearch} size="sm">
                        <SearchIcon className="w-4 h-4 me-2" />
                        Search
                    </Button>
                    <Button onClick={handleClear} variant="outline" size="sm">
                        Clear
                    </Button>
                </div>
            </Card>

            {loading && (
                <div className="flex justify-center items-center min-h-[300px]">
                    <LoadingSpinner meta={LoadingSpinnerMeta} />
                </div>
            )}

            {error && (
                <p className="text-danger-600 dark:text-danger-400">{error}</p>
            )}

            {!loading && !error && volunteers.length === 0 && (
                <p className="text-gray-500 dark:text-gray-400">No volunteers found.</p>
            )}

            {!loading && !error && volunteers.length > 0 && (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-200 dark:bg-gray-700">
                                <tr>
                                    <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Name</th>
                                    <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Phone Number</th>
                                    <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Gender</th>
                                    <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Age</th>
                                    <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Service Record</th>
                                    <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Jamat Khana</th>
                                </tr>
                            </thead>
                            <tbody>
                                {volunteers.map((volunteer) => (
                                    <tr key={volunteer.csid} className="border-b border-gray-200 dark:border-gray-700">
                                        <td className="py-3 px-6 text-gray-900 dark:text-gray-100">{volunteer.first_name} {volunteer.last_name}</td>
                                        <td className="py-3 px-6 text-gray-900 dark:text-gray-100">{volunteer.phone || 'Not Provided'}</td>
                                        <td className="py-3 px-6 text-gray-900 dark:text-gray-100">{volunteer.gender || 'Not Provided'}</td>
                                        <td className="py-3 px-6 text-gray-900 dark:text-gray-100">{volunteer.age ?? 'Not Provided'}</td>
                                        <td className="py-3 px-6 text-gray-900 dark:text-gray-100">{volunteer.service_record || 'Not Provided'}</td>
                                        <td className="py-3 px-6 text-gray-900 dark:text-gray-100">{volunteer.jamat_khana || 'Not Provided'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {!loading && !error && pagination.last_page > 1 && (
                <div className="flex justify-center items-center gap-4 mt-6">
                    <Button
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        variant="outline"
                        size="sm"
                    >
                        <ChevronLeftIcon className="w-5 h-5" />
                    </Button>

                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        Page {pagination.current_page} of {pagination.last_page}
                    </span>

                    <Button
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                        variant="outline"
                        size="sm"
                    >
                        <ChevronRightIcon className="w-5 h-5" />
                    </Button>
                </div>
            )}
        </div>
    )
}
